/**
 * Abstract UI Provider — shared `IUIProvider` implementation that
 * owns all UI state and emits `state:changed` whenever it mutates.
 * Concrete subclasses (`TuiProvider` for cell-grid CLI rendering,
 * `WebUIProvider` for canvas-based SPA rendering) are largely empty
 * token classes today; they exist for naming clarity and to give
 * each side a stable subclass to hang future side effects on.
 *
 * What the base owns:
 *   - All UI state (menu items, text lines, analog/digital values,
 *     hex dumps, user boxes, input dialog).
 *   - The `ScreenBuffer` cell grid that ink (CLI) and the SPA canvas
 *     both paint from. Each `text` / `digitalOut` / `analogOut` /
 *     `hexDump` call updates BOTH the primitive arrays AND the cell
 *     grid. Hosts pick whichever surface fits their renderer.
 *   - Menu selection / input dialog plumbing, including the
 *     `ensureConnected` / `confirmConnectError` / `scriptSelect`
 *     promise dance.
 *   - The `lineBaseRow` offset the screen executor flips between
 *     LINE blocks.
 *
 * Subclasses can still override the `protected paintX` /
 * `clearBufferX` hooks if they ever need to route output somewhere
 * other than (or in addition to) the shared `ScreenBuffer` — but the
 * default behaviour is correct for both TUI and Web today.
 */
import { EventEmitter } from 'eventemitter3';
import { getLogger } from '@emdzej/bimmerz-logger';
import { ScreenBuffer } from './screen-buffer.js';
const log = getLogger('INPAX.ui-provider');
import { initialUIState, } from './state.js';
/**
 * Format a numeric value through `analogout`'s format-spec argument.
 *
 * Two flavours of spec exist in real BMW scripts:
 *
 *   1. **INPA bare spec** — `"W.D"` (width.decimals), `"W"`, or
 *      just `".D"`. Example: MS43's `ergebnisAnalogAusgabe` passes a
 *      literal constant `"4.2"` for every analog gauge, meaning
 *      "render with 2 decimals, target width 4". This is the
 *      common case and what tripped us up before: with no `%` sign
 *      the previous regex returned the format unchanged and every
 *      gauge displayed the literal `"4.2"`.
 *   2. **C printf-style** — `"%.1f"`, `"%5.2f"`, `"%d"`, etc.
 *      Less common but seen in some scripts that embed units in
 *      the format (`"%5.1f V"`). The spec is substituted in place.
 *
 * `value.toString()` is the fallback when the format string is
 * empty or doesn't parse as either of the above.
 *
 * Width-only specs (`"5"`) integer-format with left-padding to that
 * width. Precision-only specs (`".2"`) use `toFixed` and no padding.
 * Both (`"5.2"`) use `toFixed` plus padding.
 *
 * Shared between subclasses so the TUI / canvas / overlay don't
 * disagree on rounding.
 */
export const formatAnalogValue = (value, format) => {
    if (!format)
        return value.toString();
    // C printf-style spec embedded in the format string.
    const printfRe = /%(\d+)?(?:\.(\d+))?[fdeg]/;
    const printfMatch = format.match(printfRe);
    if (printfMatch) {
        const decimalsStr = printfMatch[2];
        const widthStr = printfMatch[1];
        const decimals = decimalsStr ? parseInt(decimalsStr, 10) : undefined;
        const width = widthStr ? parseInt(widthStr, 10) : undefined;
        const conv = printfMatch[0].slice(-1);
        let rendered = conv === 'd'
            ? Math.round(value).toString()
            : decimals !== undefined
                ? value.toFixed(decimals)
                : value.toString();
        if (width !== undefined && rendered.length < width) {
            rendered = rendered.padStart(width, ' ');
        }
        return format.replace(printfMatch[0], rendered);
    }
    // INPA bare spec: `W`, `.D`, or `W.D` — width / precision pair
    // with no `%`. Tolerates surrounding whitespace.
    const best2Match = format.trim().match(/^(\d+)?(?:\.(\d+))?$/);
    if (best2Match && (best2Match[1] !== undefined || best2Match[2] !== undefined)) {
        const widthStr = best2Match[1];
        const decimalsStr = best2Match[2];
        const decimals = decimalsStr !== undefined ? parseInt(decimalsStr, 10) : undefined;
        const width = widthStr !== undefined ? parseInt(widthStr, 10) : undefined;
        let rendered = decimals !== undefined ? value.toFixed(decimals) : Math.round(value).toString();
        if (width !== undefined && rendered.length < width) {
            rendered = rendered.padStart(width, ' ');
        }
        return rendered;
    }
    // Unparsed — return as-is. Better to leak the spec than to
    // silently swallow it.
    return format;
};
export class UIProvider extends EventEmitter {
    _state;
    screenBuffer = new ScreenBuffer();
    // Width of the last write at each `row:col` — lets a shorter follow-up
    // write at the same anchor blank the previous, longer text instead of
    // leaving a trailing tail behind. Cleared on `setScreen` / `blankScreen`.
    lastWriteLengths = new Map();
    /** Internal event emitter for state changes */
    internal = new EventEmitter();
    /**
     * Row-offset applied to every output call. The screen executor
     * flips this between LINE blocks so each LINE writes inside its
     * own coordinate space — see `IUIProvider.setLineBaseRow`.
     */
    lineBaseRow = 0;
    constructor() {
        super();
        // CRUCIAL: plain spread of `initialUIState` shares its mutable
        // collections (menuItems, textLines, …) across every instance.
        // Build fresh collections per instance so each new IPO mount
        // gets a clean slate.
        this._state = {
            ...initialUIState,
            menuItems: [],
            textLines: [],
            analogValues: [],
            digitalValues: [],
            hexDumps: [],
            userBoxes: new Map(),
        };
    }
    // ============ Read-only state access ============
    /** Get current state (readonly snapshot) */
    get state() {
        return this._state;
    }
    /** Subscribe to state changes */
    onStateChange(handler) {
        this.internal.on('state:changed', handler);
        return () => this.internal.off('state:changed', handler);
    }
    getMenuItems() {
        return this._state.menuItems;
    }
    getTextLines() {
        return this._state.textLines;
    }
    getAnalogValues() {
        return this._state.analogValues;
    }
    getDigitalValues() {
        return this._state.digitalValues;
    }
    getUserBoxes() {
        return this._state.userBoxes;
    }
    getInputDialog() {
        return this._state.inputDialog;
    }
    /** Get the cell-grid buffer that ink / canvas paint from. */
    getScreenBuffer() {
        return this.screenBuffer;
    }
    update() {
        this.internal.emit('state:changed');
    }
    // ============ Template-method hooks ============
    //
    // Default impls paint into the shared ScreenBuffer. Subclasses can
    // override if they need to route output somewhere else.
    /** Called whenever a primitive is written that conceptually paints
     *  a row of text cells. Provides absolute `(row, col)` (after the
     *  lineBaseRow has been applied). */
    paintText(row, col, text, fg, bg) {
        this.writeBuffer(row, col, text, fg, bg);
    }
    /** Called by `digitalOut` after the state entry has been pushed.
     *  Paints `● label` / `○ label` into the cell grid; the canvas
     *  renders a real LED disc on top, the TUI relies on the glyph. */
    paintDigital(row, col, value, trueText, falseText, bg) {
        // INPA draws digitalout as a graphical LED-style indicator (filled
        // circle = true, empty = false) PLUS the optional trueText/falseText
        // label next to it. Scripts like UTILITY/STATUS_UBATT pass empty
        // strings for the labels and write their own "on"/"off" text via
        // ftextout — so writing only the label leaves the indicator slot
        // blank. Render the indicator glyph here too, with the label
        // appended after it. Green for on, dim red for off — matches the
        // colour convention real INPA uses.
        const glyph = value ? '●' : '○';
        const label = value ? trueText : falseText;
        const text = label ? `${glyph} ${label}` : glyph;
        const fg = value ? 2 /* green */ : 1 /* red */;
        this.writeBuffer(row, col, text, fg, bg);
    }
    /** Called by `analogOut` after the state entry has been pushed. */
    paintAnalog(row, col, formatted, fg, bg) {
        this.writeBuffer(row, col, formatted, fg, bg);
    }
    /** Called by `hexDump` after the state entry has been pushed. */
    paintHex(row, col, hex, fg, bg) {
        this.writeBuffer(row, col, hex, fg, bg);
    }
    /** Clear a rectangular region in the cell grid. */
    clearBufferRect(row, col, width, height) {
        this.screenBuffer.clearRect(row, col, width, height);
    }
    /** Clear the entire cell grid. Called by `blankScreen` / `setScreen`. */
    clearBufferAll() {
        this.screenBuffer.clear();
        this.lastWriteLengths.clear();
    }
    writeBuffer(row, col, text, fg, bg) {
        const key = `${row}:${col}`;
        const previousLength = this.lastWriteLengths.get(key);
        if (previousLength && previousLength > text.length) {
            this.screenBuffer.clearRect(row, col, previousLength, 1);
        }
        this.screenBuffer.write(row, col, text, fg, bg);
        this.lastWriteLengths.set(key, text.length);
    }
    // ============ Menu Selection (called by UI layer) ============
    /** Call when user selects a menu item */
    selectMenuItem(itemNum) {
        const item = this._state.menuItems.find(m => m.itemNum === itemNum && m.enabled);
        if (item) {
            this.emit('menu:select', { itemNum, text: item.text });
        }
    }
    /** Call when user presses back/escape in menu */
    menuBack() {
        this.emit('menu:back');
    }
    /** Submit current input dialog */
    submitInput(value) {
        if (this._state.inputResolve) {
            this._state.inputResolve(value);
            this._state.inputResolve = null;
        }
        this._state.inputDialog = null;
        // Outcome surfaced via `getinputstate` — INPA convention is
        // Win32-style return codes: **0 = success (user submitted OK)**,
        // non-zero = error / cancel.
        //
        // Verified via KOMBI.IPO m_steuern_analog ITEM 0:
        //   LOAD global[28]   ; the value getinputstate just wrote
        //   LOAD global[29]   ; initialised to int 0 in __inpa_startup__
        //   ALU EQ            ; (inputstate == 0)?
        //   MOVE 0, 1         ; condition = bool result
        //   JMPZ @51          ; skip the OK path if EQ was false
        //   …OK path…         ; only reached when cond != 0 — i.e. EQ true
        //                     ;   — i.e. inputstate == 0 — i.e. user pressed OK
        //
        // So for the OK path to execute, `getinputstate` must return 0
        // on submit — the bytecode reads "if (inputstate == 0) { run OK
        // path }". JMPZ matches INPA's `if (condition == 0) ip = target`.
        this._state.lastInputState = 0;
        this.emit('input:submit', { value });
        this.update();
    }
    /** Cancel current input dialog */
    cancelInput() {
        if (this._state.inputResolve) {
            // Cancellation value depends on dialog shape:
            //   - number: 0      (script expects a number)
            //   - scriptselect: null (script's caller branches on null vs picked IPO)
            //   - connect-error: 'continue' (cancelling the error dialog =
            //     proceed with whatever broken state we're in; matches the
            //     "X" / Escape gesture's natural meaning)
            //   - everything else: '' (string)
            let cancelValue = '';
            if (this._state.inputDialog?.type === 'number')
                cancelValue = 0;
            else if (this._state.inputDialog?.type === 'scriptselect')
                cancelValue = null;
            else if (this._state.inputDialog?.type === 'connect-error')
                cancelValue = 'continue';
            this._state.inputResolve(cancelValue);
            this._state.inputResolve = null;
        }
        this._state.inputDialog = null;
        // Cancel = non-zero error code. INPA convention treats 0 as
        // success; any non-zero is "something other than OK happened".
        // The script's `EQ inputstate, 0` check returns false here, the
        // condition register stays 0, JMPZ jumps past the OK path, and
        // the cancel branch (typically a no-op / "skip the value
        // processing") runs as intended.
        this._state.lastInputState = 1;
        this.emit('input:cancel');
        this.update();
    }
    // ============ IUIProvider implementation ============
    setScreen(handle, cyclic) {
        this._state.screenHandle = handle;
        this._state.screenCyclic = cyclic;
        this._state.textLines = [];
        this._state.analogValues = [];
        this._state.digitalValues = [];
        this._state.hexDumps = [];
        // Pagination resets on every SCREEN swap. The screen executor
        // immediately follows with `setTotalLines` / `setVisibleLineCount`
        // once it's inspected the new screen's LINE-block array.
        this._state.firstVisibleLine = 0;
        this._state.totalLines = 0;
        this.clearBufferAll();
        this.emit('screen:ready');
        this.update();
    }
    // ============ Pagination ============
    //
    // INPA's SCREEN blocks can declare more LINE blocks than fit on
    // the viewport. The screen executor reads `firstVisibleLine` each
    // cycle and shifts every LINE block's `setLineBaseRow` by that
    // amount; the keymap and the overlay buttons mutate it through
    // `scrollLines` / `scrollToTop` / `scrollToBottom`. See
    // `docs/research/screen-line-pagination.md`.
    /** Snapshot of the current first-visible-line index. Mirrors
     *  `state.firstVisibleLine` but exposed through `IUIProvider` so
     *  callers behind the interface don't need to touch the `state`
     *  getter. */
    getFirstVisibleLine() {
        return this._state.firstVisibleLine;
    }
    /** Mirror of `state.visibleLineCount` exposed through `IUIProvider`. */
    getVisibleLineCount() {
        return this._state.visibleLineCount;
    }
    /** Called by the screen executor at attach time. */
    setTotalLines(total) {
        this._state.totalLines = Math.max(0, total);
        // If the user had scrolled past the new end (script swapped to a
        // shorter screen), clamp back into range.
        const max = Math.max(0, this._state.totalLines - this._state.visibleLineCount);
        if (this._state.firstVisibleLine > max) {
            this._state.firstVisibleLine = max;
        }
        this.update();
    }
    /** Called by the host UI (canvas) once it knows how many LINE blocks
     *  fit vertically. Setting this re-clamps `firstVisibleLine`. */
    setVisibleLineCount(count) {
        this._state.visibleLineCount = Math.max(0, count);
        const max = Math.max(0, this._state.totalLines - this._state.visibleLineCount);
        if (this._state.firstVisibleLine > max) {
            this._state.firstVisibleLine = max;
        }
        this.update();
    }
    /** Move the visible window by `delta` LINE blocks. Negative = up,
     *  positive = down. Clamps to `[0, max]`. No-op when totalLines
     *  fits in the visible window. */
    scrollLines(delta) {
        if (this._state.totalLines <= this._state.visibleLineCount)
            return;
        const max = Math.max(0, this._state.totalLines - this._state.visibleLineCount);
        const next = Math.max(0, Math.min(max, this._state.firstVisibleLine + delta));
        if (next === this._state.firstVisibleLine)
            return;
        this._state.firstVisibleLine = next;
        this.update();
    }
    scrollToTop() {
        if (this._state.firstVisibleLine === 0)
            return;
        this._state.firstVisibleLine = 0;
        this.update();
    }
    scrollToBottom() {
        const max = Math.max(0, this._state.totalLines - this._state.visibleLineCount);
        if (this._state.firstVisibleLine === max)
            return;
        this._state.firstVisibleLine = max;
        this.update();
    }
    blankScreen() {
        this._state.textLines = [];
        this._state.analogValues = [];
        this._state.digitalValues = [];
        this._state.hexDumps = [];
        this.clearBufferAll();
        this.update();
    }
    clearRect(row, col, width, height) {
        const r = row + this.lineBaseRow;
        this._state.textLines = this._state.textLines.filter(l => !(l.row >= r && l.row < r + height && l.col >= col && l.col < col + width));
        this.clearBufferRect(r, col, width, height);
        this.update();
    }
    setTitle(title) {
        this._state.title = title;
        this.update();
    }
    setColor(fg, bg) {
        this._state.fg = fg;
        this._state.bg = bg;
    }
    setLineBaseRow(baseRow) {
        this.lineBaseRow = baseRow;
    }
    /**
     * Snapshot of the current foreground/background colour codes the
     * next text-output call will use. Mirrors `setColor`'s storage;
     * the dispatcher calls this for `ftextout` (whose INPA signature
     * has no colour args — `(text, row, col, fontsize, fontattr)`).
     */
    getCurrentColors() {
        return { fg: this._state.fg, bg: this._state.bg };
    }
    // === Menu ===
    setMenuTitle(title) {
        this._state.menuTitle = title;
        this.update();
    }
    setMenu(handle) {
        this._state.menuHandle = handle;
        this._state.menuItems = [];
        this.update();
    }
    setItem(itemNum, text, enabled) {
        const existing = this._state.menuItems.findIndex(m => m.itemNum === itemNum);
        const item = { itemNum, text, enabled };
        if (existing >= 0) {
            this._state.menuItems[existing] = item;
        }
        else {
            this._state.menuItems.push(item);
            this._state.menuItems.sort((a, b) => a.itemNum - b.itemNum);
        }
        this.update();
    }
    setItemRepeat(itemNum, enabled) {
        const item = this._state.menuItems.find(m => m.itemNum === itemNum);
        if (item) {
            item.enabled = enabled;
            this.update();
        }
    }
    // === Text Output ===
    text(row, col, text) {
        const r = row + this.lineBaseRow;
        this._state.textLines.push({
            row: r,
            col,
            text,
            fg: this._state.fg,
            bg: this._state.bg,
        });
        this.paintText(r, col, text, this._state.fg, this._state.bg);
        this.update();
    }
    textOut(text, row, col) {
        this.text(row, col, text);
    }
    fTextOut(text, row, col, fg, bg, fontSize, fontAttr) {
        const r = row + this.lineBaseRow;
        this._state.textLines.push({ row: r, col, text, fg, bg, fontSize, fontAttr });
        this.paintText(r, col, text, fg, bg);
        this.update();
    }
    fTextClear(text, row, col, _textSize, _textAttr) {
        const r = row + this.lineBaseRow;
        this._state.textLines = this._state.textLines.filter(l => !(l.row === r && l.col === col && l.text === text));
        this.clearBufferRect(r, col, text.length, 1);
        this.update();
    }
    hexDump(row, col, data, len) {
        const r = row + this.lineBaseRow;
        const hex = Array.from(data.slice(0, len))
            .map(b => b.toString(16).padStart(2, '0').toUpperCase())
            .join(' ');
        this._state.hexDumps.push({ row: r, col, data: hex });
        this.paintHex(r, col, hex, this._state.fg, this._state.bg);
        this.update();
    }
    // === Data Output ===
    digitalOut(value, row, col, trueText, falseText) {
        const r = row + this.lineBaseRow;
        this._state.digitalValues = this._state.digitalValues.filter(d => !(d.row === r && d.col === col));
        this._state.digitalValues.push({ row: r, col, value, trueText, falseText });
        this.paintDigital(r, col, value, trueText, falseText, this._state.bg);
        this.update();
    }
    analogOut(value, row, col, min, max, minValid, maxValid, format) {
        const r = row + this.lineBaseRow;
        this._state.analogValues = this._state.analogValues.filter(a => !(a.row === r && a.col === col));
        this._state.analogValues.push({
            row: r,
            col,
            value,
            min,
            max,
            minValid,
            maxValid,
            format,
        });
        const formatted = formatAnalogValue(value, format);
        this.paintAnalog(r, col, formatted, this._state.fg, this._state.bg);
        this.update();
    }
    /**
     * `multianalogout` — same plumbing as `analogOut` with one extra
     * `mode` field stored on the resulting entry. Real INPA's docs
     * (`ref/Inpa.h:201`) declare it as the canonical multi-value
     * variant; with no script in our test set actually using a
     * non-zero mode we render identically to a regular `analogout`
     * for now. The mode value survives in `state.analogValues` so the
     * canvas (or any future renderer) can branch on it without going
     * back through the provider.
     */
    multiAnalogOut(value, row, col, min, max, minValid, maxValid, format, mode) {
        const r = row + this.lineBaseRow;
        this._state.analogValues = this._state.analogValues.filter(a => !(a.row === r && a.col === col));
        this._state.analogValues.push({
            row: r,
            col,
            value,
            min,
            max,
            minValid,
            maxValid,
            format,
            mode,
        });
        const formatted = formatAnalogValue(value, format);
        this.paintAnalog(r, col, formatted, this._state.fg, this._state.bg);
        this.update();
    }
    // === Input ===
    getInputState() {
        // Surfaces the outcome of the most recently completed input
        // dialog, not the open/closed flag of the current dialog. Real
        // INPA bytecode (e.g. KOMBI.IPO m_steuern_analog ITEM 0) calls
        // `getinputstate` AFTER `inputint` resolves, then branches on
        // `inputstate != 0` for the success path. Returning the
        // dialog-open flag here used to make every submission look like
        // a cancel because the dialog was already closed by then.
        return this._state.lastInputState;
    }
    async inputText(title, text) {
        return new Promise(resolve => {
            this._state.inputDialog = { type: 'text', title, text, value: '' };
            this._state.inputResolve = resolve;
            this.update();
        });
    }
    async input2Text(title, text, _l1, _l2) {
        const result = await this.inputText(title, text);
        return [result, ''];
    }
    async inputNum(title, text, min, max) {
        return new Promise(resolve => {
            this._state.inputDialog = { type: 'number', title, text, value: '', min, max };
            this._state.inputResolve = v => resolve(Number(v) || min);
            this.update();
        });
    }
    async inputInt(title, text, min, max) {
        const result = await this.inputNum(title, text, min, max);
        return Math.floor(result);
    }
    async input2Int(title, text, _l1, _l2, min1, max1, min2, _max2) {
        const result = await this.inputInt(title, text, min1, max1);
        return [result, min2];
    }
    async togglelist(multipleSelect, argNum, items) {
        return new Promise(resolve => {
            this._state.inputDialog = {
                type: 'toggle-list',
                // Title matches the real INPA dialog header verbatim — keeps
                // the muscle memory intact for users coming from BMW INPA and
                // makes screenshots line up across tools.
                title: 'Please select the objects to be controlled',
                text: '',
                value: '',
                toggleItems: items,
                toggleMultipleSelect: multipleSelect,
                toggleArgNum: argNum,
            };
            // The dialog component does the encoding (OR'd masks or
            // space-separated indices, per argNum) and calls
            // submitInput(<encoded string>) on OK / cancelInput() on
            // cancel. We just resolve with whatever it passes us.
            this._state.inputResolve = v => resolve(String(v ?? ''));
            this.update();
        });
    }
    async inputHex(title, text, min, max) {
        return new Promise(resolve => {
            this._state.inputDialog = { type: 'hex', title, text, value: '', min, max };
            this._state.inputResolve = v => resolve(String(v) || min);
            this.update();
        });
    }
    async input2Hex(title, text, _l1, _l2, min1, _max1, min2, _max2) {
        const result = await this.inputHex(title, text, min1, '');
        return [result, min2];
    }
    async input2HexNum(title, text, _hexLabel, _numLabel, minHex, _maxHex, minNum, _maxNum) {
        const result = await this.inputHex(title, text, minHex, '');
        return [result, minNum];
    }
    async inputDigital(title, text, offText, onText) {
        return new Promise(resolve => {
            this._state.inputDialog = {
                type: 'digital',
                title,
                text,
                value: '',
                trueText: onText,
                falseText: offText,
            };
            this._state.inputResolve = v => resolve(Boolean(v));
            this.update();
        });
    }
    // === Message Boxes ===
    async messageBox(title, text) {
        return new Promise(resolve => {
            this._state.inputDialog = { type: 'message', title, text, value: '' };
            this._state.inputResolve = () => resolve();
            this.update();
        });
    }
    async infoBox(title, text) {
        return this.messageBox(title, text);
    }
    /**
     * Drive the host's connect flow. Sets a `connect`-shaped
     * inputDialog so the host's settings panel can auto-open. Resolves
     * when the host calls `submitInput()` (typically once the cable
     * has been opened). Cancel is treated as "user chose to proceed
     * without a fresh connection"; the dispatcher's `INPAapiInit`
     * loop catches subsequent `ediabas.init()` failures separately.
     */
    async ensureConnected() {
        return new Promise(resolve => {
            this._state.inputDialog = {
                type: 'connect',
                title: 'EDIABAS connection',
                text: '',
                value: '',
            };
            this._state.inputResolve = () => resolve();
            this.update();
        });
    }
    /**
     * Surface a connection error and ask the user how to proceed.
     * `submitInput` resolves with the chosen string outcome; the
     * dispatcher's INPAapiInit loop interprets that.
     */
    async confirmConnectError(message) {
        return new Promise(resolve => {
            this._state.inputDialog = {
                type: 'connect-error',
                title: 'EDIABAS connection failed',
                text: message,
                value: '',
            };
            this._state.inputResolve = value => {
                const choice = value === 'retry' || value === 'stop' ? value : 'continue';
                resolve(choice);
            };
            this.update();
        });
    }
    /**
     * Show the script-select picker. The provider doesn't read the
     * .ENG/.GER INI itself — it hangs an `inputDialog` of type
     * `scriptselect` carrying the requested filename and waits. The
     * host-side dialog component is expected to:
     *
     *   1. Read the INI file from its own filesystem surface.
     *   2. Parse it (sections → tree, `ENTRY=` → leaves).
     *   3. Render the tree + entries.
     *   4. On confirm, call `ui.submitInput(<picked-ipo-basename>)`.
     *   5. On cancel, call `ui.cancelInput()`.
     */
    async scriptSelect(iniFile) {
        return new Promise(resolve => {
            this._state.inputDialog = {
                type: 'scriptselect',
                title: 'Select script',
                text: iniFile,
                value: '',
                scriptSelectFile: iniFile,
            };
            this._state.inputResolve = value => {
                if (value === null || value === undefined) {
                    resolve(null);
                }
                else {
                    resolve(String(value));
                }
            };
            this.update();
        });
    }
    // === User boxes (state-only — drawing is host-specific) ===
    userBoxOpen(boxNum, row, col, height, width, title, _text) {
        log.debug({ boxNum, title, before: this._state.userBoxes.size }, 'userBoxOpen');
        this._state.userBoxes.set(boxNum, {
            boxNum,
            row,
            col,
            height,
            width,
            title,
            lines: [],
            fg: this._state.fg,
            bg: this._state.bg,
            visible: true,
        });
        this.update();
    }
    userBoxClose(boxNum) {
        log.debug({ boxNum, had: this._state.userBoxes.has(boxNum), before: this._state.userBoxes.size }, 'userBoxClose');
        this._state.userBoxes.delete(boxNum);
        this.emit('messagebox:closed', { boxNum });
        this.update();
    }
    userBoxFTextOut(boxNum, text, row, col, fg, bg) {
        const box = this._state.userBoxes.get(boxNum);
        if (box) {
            box.lines.push({ row, col, text, fg, bg });
            this.update();
        }
    }
    userBoxClear(boxNum) {
        const box = this._state.userBoxes.get(boxNum);
        if (box) {
            box.lines = [];
            this.update();
        }
    }
    userBoxSetColor(boxNum, fg, bg) {
        const box = this._state.userBoxes.get(boxNum);
        if (box) {
            box.fg = fg;
            box.bg = bg;
            this.update();
        }
    }
}
//# sourceMappingURL=ui-provider.js.map